/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { getSession, signIn } from 'next-auth/react'
import { useRouter } from 'next/router' // Import useRouter untuk pengalihan
import { destroyCookie, parseCookies } from 'nookies' // Import nookies untuk mengelola cookie
import { toast } from 'react-toastify' // Import toast untuk notifikasi
import 'react-toastify/dist/ReactToastify.css' // Import CSS untuk toast
import { useDispatch } from 'react-redux'
import { setUser, clearUser } from '@/store/userSlice' // Pastikan clearUser diimport
import { ApiUrl, TOKEN_HEADER } from '../config/Config'

const roles = [
  { id: 1, text: 'Mahasiswa', slug: 'MAHASISWA' },
  { id: 2, text: 'Dosen', slug: 'DOSEN' },
  { id: 3, text: 'Admin', slug: 'ADMIN' },
]

export const useLogin = () => {
  const router = useRouter() // Inisialisasi router
  const [isOpen, setIsOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState(roles[0])
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState<number | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const dispatch = useDispatch() // Inisialisasi dispatch

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm()

  // Function to generate captcha
  const generateCaptcha = () => {
    let num1, num2, newAnswer

    // Menjaga agar hasil penjumlahan tidak lebih dari 30
    do {
      num1 = Math.floor(Math.random() * 30) // Menghasilkan angka 0 - 29
      num2 = Math.floor(Math.random() * 30) // Menghasilkan angka 0 - 29
      newAnswer = num1 + num2
    } while (newAnswer > 30) // Jika hasil lebih dari 30, ulangi penghitungan

    const newQuestion = `${num1} + ${num2} = ?`

    setQuestion(newQuestion)
    setAnswer(newAnswer)

    // Drawing on canvas
    const canvas = canvasRef.current
    if (canvas) {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.fillStyle = '#fff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.fillStyle = '#000'
        ctx.font = '20px Arial'
        ctx.fillText(newQuestion, 50, 30)
      }
    }
  }

  useEffect(() => {
    generateCaptcha()
  }, [])

  const toggleDropdown = () => setIsOpen(!isOpen)

  const handleSelect = (role: any) => {
    setSelectedRole(role)
    setIsOpen(false)
    setValue('identifier', '') // Reset identifier on role change
  }

  const onSubmit = async (data: any) => {
    // Check if captcha answer is correct
    if (parseInt(userAnswer) !== answer) {
      generateCaptcha() // Generate new captcha if answer is wrong
      toast.error('Captcha Salah!') // Tampilkan pesan error
      return // Hentikan eksekusi jika captcha salah
    }

    // Lakukan login menggunakan NextAuth.js
    const result = await signIn('credentials', {
      redirect: false,
      userNameOrEmail: data.identifier,
      password: data.password,
      role: selectedRole.slug,
      headers: {
        // Menambahkan header kustom
        token_lpppu_usu: TOKEN_HEADER,
      },
    })
    if (result?.error) {
      toast.error('Login gagal: ' + result.error) // Tampilkan pesan error
    } else {
      const session = (await getSession()) as any // Ambil session setelah login

      // Jika login berhasil, dispatch action untuk menyimpan pengguna ke Redux
      if (session && session.user) {
        const userRole = session.user?.role

        // Periksa apakah role sesuai
        if (userRole !== selectedRole.slug) {
          toast.error('User role tidak sesuai!', { autoClose: 3000 })
          destroyCookie(null, 'nextSession') // Hapus cookie jika ada
          dispatch(clearUser()) // Hapus pengguna dari Redux
          return // Hentikan eksekusi lebih lanjut
        }

        dispatch(setUser(session)) // Simpan pengguna ke Redux
        toast.success('Login berhasil!')

        // Redirect berdasarkan peran pengguna
        switch (userRole) {
          case 'MAHASISWA':
            router.push('/')
            break
          case 'DOSEN':
            router.push('/dashboard-dosen')
            break
          case 'ADMIN':
            router.push('/dashboard-admin')
            break
          default:
            router.push('/') // Redirect ke halaman utama jika peran tidak dikenali
            break
        }

        // Mengelola cookie jika diperlukan
        const cookies = parseCookies()
        if (cookies.nextSession) {
          destroyCookie(null, 'nextSession') // Hapus cookie jika ada
        }
      }
    }
  }

  return {
    isOpen,
    selectedRole,
    question,
    answer,
    userAnswer,
    canvasRef,
    errors,
    register,
    handleSubmit,
    setValue,
    toggleDropdown,
    handleSelect,
    onSubmit,
    setUserAnswer,
    roles,
  }
}
